/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useTranslation } from "react-i18next";

import { orderBy } from "lodash";
import Typography from "@mui/material/Typography";

import { colors } from "@dotnet/telematics-design-system";

import AccordionDetailsCard from "../../../../../../components/DetailsSection/components/DetailsContent/components/AccordionDetailsCard/AccordionDetailsCard";
import Separator from "../../../../../../components/Separator/Separator";
import { ROUTES, useDateTime } from "../../../../../../shared";
import { CreditsDto } from "../../../types";
import {
  checkIfPositiveNumber,
  generateNumberStringWithPlusOrMinus
} from "../../functions";
import { useStyles } from "../../styles";

interface CreditsHistoryInfoProps {
  creditsData: CreditsDto;
  renderFormattedNumber: (number: number) => string;
}

const CreditsHistoryInfo = ({
  creditsData,
  renderFormattedNumber
}: CreditsHistoryInfoProps) => {
  const { formattedDateWithoutLocalTimeZone, currentDateFormat } =
    useDateTime();
  const { t } = useTranslation();

  const { dialogContentSectionTitle, singleAccordionItemBlock, itemSeparator } =
    useStyles({});

  const formattedNumberColor = (
    positiveNumber: boolean,
    incoming?: boolean
  ) => {
    const positiveIncomingNumber = positiveNumber && incoming;
    const incomingNumberColor = positiveIncomingNumber && colors.green900;
    const positiveNumberColor = positiveNumber && colors.textDark;
    const negativeNumberColor = colors.red800;

    return incomingNumberColor || positiveNumberColor || negativeNumberColor;
  };

  const transactionsAscending = orderBy(
    creditsData.history.flatMap(item => item.transactions),
    ["date"],
    ["desc"]
  );

  return (
    <>
      <div css={css(dialogContentSectionTitle)}>
        <Typography variant="body2Medium" sx={{ color: colors.textDark }}>
          {t("General##history")}
        </Typography>
      </div>

      <AccordionDetailsCard
        route={ROUTES.Dashboard}
        showTitleArea={false}
        customStyle={{ margin: 0, padding: "16px" }}
        testId="credits-history-accordion-details"
      >
        {transactionsAscending.map((transaction, index) => (
          <div key={transaction.date}>
            <div css={css(singleAccordionItemBlock)}>
              <div
                css={css({
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px"
                })}
              >
                <Typography variant="body2Medium" color={colors.textDark}>
                  {t(
                    `Dashboard##credits history actions##${transaction.action}`
                  )}
                </Typography>

                <Typography
                  variant="body3Medium"
                  component="div"
                  color={colors.textSecondary}
                  css={css({ marginBottom: "8px" })}
                >
                  {formattedDateWithoutLocalTimeZone(
                    transaction.date,
                    currentDateFormat
                  ) || ""}
                </Typography>
              </div>
              <Typography
                variant="body2Medium"
                color={formattedNumberColor(
                  checkIfPositiveNumber(transaction.amount),
                  true
                )}
              >
                {generateNumberStringWithPlusOrMinus(
                  renderFormattedNumber(transaction.amount),
                  transaction.amount
                )}
              </Typography>
            </div>

            {index < transactionsAscending.length - 1 && (
              <Separator color={colors.gray200} css={css(itemSeparator)} />
            )}
          </div>
        ))}
      </AccordionDetailsCard>
    </>
  );
};

export default CreditsHistoryInfo;
